import React, { Children, useMemo, ReactNodeArray, useEffect, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { typography } from '@clds/typography';
import { usePhone } from '@clds/style-utils';
import { messages } from '../i18n';

export const StyledLink = styled.a`
  ${typography.xs.url};
  cursor: pointer;
  vertical-align: top;
  display: block;
  white-space: nowrap;
`;

const NUM_COLUMNS_DESKTOP = 3;
const NUM_COLUMNS_MOBILE = 2;

const getChildrenLimit = (isPhone: boolean, recordsPerColumn: number) =>
  isPhone ? recordsPerColumn * NUM_COLUMNS_MOBILE : recordsPerColumn * NUM_COLUMNS_DESKTOP - 1;
const getVisibleChildren = (children: ReactNodeArray, limit: number, allChildrenVisible: boolean) =>
  !allChildrenVisible ? children.slice(0, limit) : children;

export const areAllChildrenVisible = (recordsPerColumn: number, childrenLength: number, limit: number, showGradually?: boolean) =>
  !showGradually || recordsPerColumn * 2 - 1 > childrenLength || childrenLength - 1 <= limit;

export interface UseSectionChildrenProps {
  children: ReactNodeArray;
  recordsPerColumn: number;
  showGradually?: boolean;
  onShowMore?: () => void;
}

export const useLimitedChildren = ({ children, recordsPerColumn, showGradually, onShowMore }: UseSectionChildrenProps) => {
  const isPhone = usePhone();
  const childrenArray = Children.toArray(children);
  const [limit, setLimit] = useState(getChildrenLimit(isPhone, recordsPerColumn));
  const allChildrenVisible = areAllChildrenVisible(recordsPerColumn, childrenArray.length, limit, showGradually);
  const visibleChildren = useMemo(() => getVisibleChildren(childrenArray, limit, allChildrenVisible), [childrenArray, limit, allChildrenVisible]);
  const onClick = useCallback(() => {
    setLimit(childrenArray.length);
    onShowMore?.();
  }, [childrenArray.length, onShowMore]);

  useEffect(() => {
    if (!allChildrenVisible) {
      setLimit(getChildrenLimit(isPhone, recordsPerColumn));
    }
  }, [isPhone, recordsPerColumn, allChildrenVisible]);

  return [
    visibleChildren,
    visibleChildren.length,
    allChildrenVisible ? null : (
      <StyledLink onClick={onClick}>
        <FormattedMessage {...messages.linkShowMore} />
      </StyledLink>
    ),
  ] as [ReactNodeArray, number, typeof StyledLink | null];
};
